@if (activeGuid()) {
  <div class="client-metadata">
    @if (showSkeleton()) {
      <!-- Primary-bar skeleton on cache miss; accordion is not rendered, so not expandable (CMP-13) -->
      <div class="bn-metadata-skeleton flex items-center gap-3 h-full px-4" data-testid="metadata-skeleton">
        <!-- Avatar skeleton omitted — initials already shown in the client list -->
        <!-- <p-skeleton shape="circle" size="2rem" styleClass="flex-shrink-0" /> -->
        <div class="flex flex-col gap-1">
          <p-skeleton width="12rem" height="0.875rem" />
          <p-skeleton width="8rem" height="0.75rem" />
        </div>
      </div>
    } @else if (showRetry()) {
      <!-- Toast carries the error copy; keep a neutral retry affordance (CMP-12) -->
      <div class="flex items-center gap-3 h-full px-4" data-testid="metadata-retry">
        <button
          pButton
          type="button"
          icon="pi pi-refresh"
          label="Retry"
          class="p-button-sm p-button-text"
          (click)="refresh($event)">
        </button>
      </div>
    } @else if (header(); as h) {
      <p-accordion [value]="accordionValue()" (valueChange)="accordionValue.set($event)">
        <p-accordion-panel value="1">
          <p-accordion-header>
            <div class="bn-metadata-primary flex items-center justify-between w-full min-w-0 pr-2">
              <!-- Left: name/employer, badges (VIP → Eligible → DNC); avatar omitted — see client list -->
              <div class="flex items-center gap-3 min-w-0">
                <!-- metadata-avatar hidden for now; initials shown in the client list -->

                <div class="flex flex-col min-w-0">
                  <span class="bn-metadata-name font-semibold truncate leading-tight" data-testid="metadata-name"
                    >{{ h.firstName }} {{ h.lastName }}</span
                  >
                  @if (h.company) {
                    <span
                      class="bn-metadata-company text-xs truncate leading-tight"
                      data-testid="metadata-company"
                      >{{ h.company | capitalizeFirst }}</span
                    >
                  }
                </div>

                <div class="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                  @if (h.vipStatus === 'active') {
                    <span class="bn-metadata-badge bn-metadata-badge--vip" data-testid="badge-vip">
                      <i class="pi pi-star-fill text-xs mr-1"></i>VIP</span
                    >
                  }

                  @if (isEligible() === true) {
                    <span
                      class="bn-metadata-badge bn-metadata-badge--eligible flex-shrink-0"
                      data-testid="badge-eligible"
                      pTooltip="This client is eligible for Brightside services"
                      tooltipPosition="bottom"
                      ><span class="bn-metadata-badge-dot" aria-hidden="true"></span>Eligible</span
                    >
                  } @else if (isEligible() === false) {
                    <span
                      class="bn-metadata-badge bn-metadata-badge--not-eligible flex-shrink-0"
                      data-testid="badge-not-eligible"
                      pTooltip="This client is not eligible for Brightside services"
                      tooltipPosition="bottom"
                      >Not eligible</span
                    >
                  }

                  @if (h.doNotContact === true) {
                    <span class="bn-metadata-badge bn-metadata-badge--dnc flex-shrink-0" data-testid="badge-dnc">
                      <i class="pi pi-ban text-xs mr-1"></i>Do not contact</span
                    >
                  }
                </div>
              </div>

              <!-- Right: Salesforce / Intercom (etc.) when available — refresh lives in the GUID footer -->
              @if (clientLinks().length) {
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  @for (link of clientLinks(); track link.label) {
                    <button
                      type="button"
                      class="bn-metadata-icon-btn"
                      [class.opacity-50]="!link.url"
                      [disabled]="!link.url"
                      [attr.data-testid]="'link-' + link.label.toLowerCase()"
                      [pTooltip]="link.label"
                      tooltipPosition="bottom"
                      (click)="openLink($event, link.url)">
                      <img
                        [src]="'assets/brightnav/icons/' + link.label.toLowerCase() + '_icon.svg'"
                        [alt]="link.label + ' icon'"
                        class="w-5 h-5"
                      />
                    </button>
                  }
                </div>
              }
            </div>
          </p-accordion-header>

          <p-accordion-content>
            <div class="bn-metadata-details pt-0 text-sm">
              <!-- Detail fields grid (two rows on wide screens) with subtle dividers -->
              <div class="bn-metadata-grid">
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">DOB / Age</div>
                  <div class="bn-metadata-value" data-testid="field-dob-age">{{ dobAgeDisplay() }}</div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">SSN (last 4)</div>
                  <div class="bn-metadata-value" data-testid="field-ssn">{{ ssnDisplay() }}</div>
                </div>
                <div class="bn-metadata-field bn-metadata-field--wide">
                  <div class="bn-metadata-label">Address</div>
                  <div class="bn-metadata-value" data-testid="field-address">{{ h.address || emDash }}</div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Phone</div>
                  <div class="bn-metadata-value" data-testid="field-phone">{{ h.phone || emDash }}</div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Gender</div>
                  <div class="bn-metadata-value" data-testid="field-gender">{{ h.gender || emDash }}</div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Preferred language</div>
                  <div class="bn-metadata-value" data-testid="field-language">{{ h.preferredLanguage || emDash }}</div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Employment status</div>
                  <div class="bn-metadata-value" data-testid="field-employment-status">
                    {{ h.employmentStatus || emDash }}
                  </div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Salary</div>
                  <div class="bn-metadata-value" data-testid="field-salary">
                    @if (h.annualPay !== null && h.annualPay !== undefined) {
                      {{ h.annualPay | currency: 'USD' : 'symbol' : '1.0-0' }}/yr
                    } @else {
                      {{ emDash }}
                    }
                  </div>
                </div>
                <div class="bn-metadata-field">
                  <div class="bn-metadata-label">Household</div>
                  <div class="bn-metadata-value" data-testid="field-household">
                    @if (h.householdCount) {
                      <button
                        type="button"
                        class="bn-metadata-link"
                        data-testid="household-link"
                        (click)="openHouseholdModal()">
                        {{ h.householdCount }}
                      </button>
                    } @else {
                      {{ emDash }}
                    }
                  </div>
                </div>
              </div>

              <!-- GUID row — bottom of the expanded panel, never on the primary bar (OQ-10) -->
              <div class="bn-metadata-guid">
                <div class="bn-metadata-guid-left">
                  <span class="bn-metadata-label">GUID</span>
                  <span class="bn-metadata-guid-value font-mono break-all text-xs" data-testid="guid-value">{{
                    h.guid
                  }}</span>
                  <button
                    type="button"
                    class="bn-metadata-copy-btn"
                    data-testid="guid-copy"
                    pTooltip="Copy GUID"
                    tooltipPosition="top"
                    (click)="copyToClipboard(h.guid)">
                    <i class="pi pi-copy"></i>
                  </button>
                </div>
                <button
                  type="button"
                  class="bn-metadata-footer-refresh"
                  data-testid="metadata-refresh"
                  pTooltip="Refresh data"
                  tooltipPosition="top"
                  [disabled]="loading()"
                  (click)="refresh($event)">
                  <i [class]="loading() ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"></i>
                </button>
              </div>
            </div>
          </p-accordion-content>
        </p-accordion-panel>
      </p-accordion>

      <bn-household-members-modal
        [guid]="h.guid"
        [activeSessionGuid]="activeGuid()"
        [(visible)]="householdModalVisible" />
    }
  </div>
}
